import React, { useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import { listarFacturas, anularFactura, obtenerCaiActivo, estadoCai, libroVentasCsv, descargarTexto } from '../services/facturasService';
import { armarFacturaPdf } from '../utils/facturaPdf';
import { descargarPdf } from '../utils/reciboPdf';
import { mensajeAmigable } from '../utils/errores';

// Facturas con CAI emitidas por mes: reimprimir, anular y exportar el libro de
// ventas (CSV para Excel) con el resumen del ISV para la declaración mensual.
const L = (n) => `L ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const mesActual = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

export default function FacturasView({ onVolver, onIrConfiguracion }) {
  const { mostrarToast } = useToast();
  const [mes, setMes] = useState(mesActual());
  const [facturas, setFacturas] = useState(null);
  const [cai, setCai] = useState(undefined);
  const [error, setError] = useState(null);
  const [ocupado, setOcupado] = useState(null);

  const cargar = () => {
    setFacturas(null); setError(null);
    listarFacturas(mes).then(setFacturas).catch((e) => setError(mensajeAmigable(e)));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [mes]);
  useEffect(() => { obtenerCaiActivo().then(setCai).catch(() => setCai(null)); }, []);

  const validas = (facturas || []).filter((f) => f.estado === 'emitida');
  const suma = (k) => validas.reduce((s, f) => s + Number(f[k] || 0), 0);
  const est = cai ? estadoCai(cai) : null;

  const imprimir = async (f) => {
    setOcupado(f.id);
    try { descargarPdf(await armarFacturaPdf(f)); } catch (e) { mostrarToast(mensajeAmigable(e), 'error'); } finally { setOcupado(null); }
  };
  const anular = async (f) => {
    const motivo = window.prompt(`Anular la factura ${f.numero}.\nNo se borra: queda guardada con la leyenda ANULADA.\n\nEscribe el motivo:`);
    if (motivo === null) return;
    if (!motivo.trim()) { mostrarToast('Escribe el motivo de la anulación.', 'error'); return; }
    setOcupado(f.id);
    try { await anularFactura(f.id, motivo); mostrarToast(`Factura ${f.numero} anulada`, 'exito'); cargar(); }
    catch (e) { mostrarToast(String(e?.message || '').includes('SOLO_LA_DUENA') ? 'Solo la dueña puede anular facturas.' : mensajeAmigable(e), 'error'); }
    finally { setOcupado(null); }
  };
  const exportar = () => {
    if (!facturas?.length) { mostrarToast('No hay facturas en ese mes.', 'error'); return; }
    descargarTexto(`Libro-ventas-${mes}.csv`, libroVentasCsv(facturas));
  };

  return (
    <div className="k-page">
      <div className="k-page-head">
        <h2 className="k-page-title">🏛️ Facturas</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="month" value={mes} max={mesActual()} onChange={(e) => e.target.value && setMes(e.target.value)}
            aria-label="Mes" style={{ padding: '8px', border: '1px solid #D1D5DB', borderRadius: '8px', fontFamily: 'inherit' }} />
          <button onClick={exportar} className="k-btn-soft">⬇️ Libro de ventas (Excel)</button>
          <button onClick={onVolver} className="k-btn-soft">← Volver</button>
        </div>
      </div>

      {cai === null && (
        <div className="k-card" style={{ padding: '14px', marginBottom: '14px', background: '#FFFBEB', border: '1px solid #FDE68A' }}>
          Aún no tienes un CAI registrado. <button onClick={onIrConfiguracion} className="k-btn-soft" style={{ marginLeft: '6px' }}>⚙️ Registrar CAI</button>
        </div>
      )}
      {est && (!est.puede || est.aviso) && (
        <div className="k-card" style={{ padding: '14px', marginBottom: '14px', background: est.puede ? '#FFFBEB' : '#FEF2F2', border: `1px solid ${est.puede ? '#FDE68A' : '#FCA5A5'}` }}>
          {est.motivo === 'vencido' && '⛔ Tu CAI venció. Registra el nuevo en ⚙️ Configuración para seguir facturando.'}
          {est.motivo === 'agotado' && '⛔ Se acabó tu rango de facturas. Registra el nuevo CAI en ⚙️ Configuración.'}
          {est.aviso === 'por_vencer' && `⚠️ Tu CAI vence en ${est.dias} días: solicita el siguiente al SAR.`}
          {est.aviso === 'por_agotarse' && `⚠️ Te quedan ${est.quedan} facturas en el rango: solicita el siguiente CAI.`}
        </div>
      )}

      <div className="k-kpis" style={{ marginBottom: '16px' }}>
        <div className="k-tile"><span className="lbl">Total facturado</span><span className="num">{L(suma('total'))}</span><span className="sub">{validas.length} factura(s){facturas && facturas.length > validas.length ? ` · ${facturas.length - validas.length} anulada(s)` : ''}</span></div>
        <div className="k-tile"><span className="lbl">ISV cobrado</span><span className="num">{L(suma('isv_15') + suma('isv_18'))}</span><span className="sub">15%: {L(suma('isv_15'))} · 18%: {L(suma('isv_18'))}</span></div>
        <div className="k-tile"><span className="lbl">Ventas gravadas</span><span className="num">{L(suma('importe_gravado_15') + suma('importe_gravado_18'))}</span><span className="sub">Exento: {L(suma('importe_exento'))} · Exonerado: {L(suma('importe_exonerado'))}</span></div>
      </div>

      {error && <p style={{ color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</p>}
      {!facturas && !error && <p style={{ color: '#6B7280' }}>⏳ Cargando facturas…</p>}
      {facturas && facturas.length === 0 && (
        <div className="k-card" style={{ padding: '28px', textAlign: 'center', color: '#6B7280' }}>
          No hay facturas en este mes. Se emiten desde <b>Ventas de hoy</b> (botón 🧾) o al cobrar en <b>Vender</b>.
        </div>
      )}
      {facturas && facturas.length > 0 && (
        <div className="k-card" style={{ padding: '4px 16px' }}>
          {facturas.map((f) => (
            <div key={f.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #F3F0FA', flexWrap: 'wrap', opacity: f.estado === 'anulada' ? 0.6 : 1 }}>
              <div style={{ flex: 1, minWidth: '180px' }}>
                <div style={{ fontWeight: 700, fontFamily: "'DM Mono', monospace", fontSize: '13.5px' }}>
                  {f.numero} {f.estado === 'anulada' && <span style={{ color: '#B91C1C', fontFamily: 'inherit' }}>· ANULADA</span>}
                </div>
                <div style={{ fontSize: '12.5px', color: '#6B7280' }}>
                  {new Date(f.fecha_emision).toLocaleString('es-HN', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit' })} · {f.cliente_nombre}{f.cliente_rtn ? ` · RTN ${f.cliente_rtn}` : ''}
                  {f.estado === 'anulada' && f.motivo_anulacion ? ` · Motivo: ${f.motivo_anulacion}` : ''}
                </div>
              </div>
              <div style={{ fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>{L(f.total)}</div>
              <button onClick={() => imprimir(f)} disabled={ocupado === f.id} className="k-btn-soft" title="Descargar PDF">⬇️</button>
              {f.estado === 'emitida' && <button onClick={() => anular(f)} disabled={ocupado === f.id} className="k-btn-soft" style={{ color: '#B91C1C' }} title="Anular">⛔</button>}
            </div>
          ))}
        </div>
      )}
      <p style={{ marginTop: '12px', fontSize: '12px', color: '#9CA3AF' }}>
        Las facturas no se borran: si una tiene un error, se anula y queda guardada (art. 41). Guarda tus copias por el plazo que indica el Código Tributario.
      </p>
    </div>
  );
}
